<template>
  <div id="listWidget" class="mb-5 firmware-update-page">
    <!-- 정보 카드 -->
    <div v-if="controllers?.length > 0" class="info-card card-item mb-4">
      <div class="info-card-header">
        <i class="fa-solid fa-info-circle me-2 text-primary"></i>
        <span class="info-card-title">업데이트 정보</span>
      </div>
      <div class="info-card-body">
        <div class="info-item">
          <span class="info-label">업데이트 가능 장치:</span>
          <span class="info-value">
            <span class="update-count-badge">{{ controllers?.length || 0 }}개</span>
          </span>
        </div>
        <div class="info-item">
          <span class="info-label">예상 소요 시간:</span>
          <span class="info-value">약 2~3분</span>
        </div>
      </div>
    </div>

    <!-- 빈 상태 -->
    <div v-if="controllers?.length === 0 && allControllers?.length === 0" class="empty-state">
      <div class="empty-state-icon">
        <i class="fa-regular fa-circle-check"></i>
      </div>
      <div class="empty-state-title">모든 장치가 최신 버전입니다</div>
      <div class="empty-state-description">
        펌웨어 업데이트가 필요한 장치가 없습니다.
      </div>
    </div>

    <!-- 모든 장치가 최신 버전일 때 -->
    <div class="row" v-if="allControllersUpToDate">
      <div class="col-md-6 mb-3">
        <div class="info-card card-item">
          <div class="info-card-header">
            <i class="fa-regular fa-circle-check me-2 text-success"></i>
            <span class="info-card-title">펌웨어 상태</span>
          </div>
          <div class="info-card-body">
            <div class="info-item">
              <div class="info-value text-success">
                <i class="fa-solid fa-check me-2"></i>
                모든 장치의 펌웨어가 최신 버전입니다.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 컨트롤러 목록 -->
    <div class="row" v-if="allControllers?.length > 0">
      <UpdateControllerItem
        v-for="(item, index) in allControllers"
        :key="item._id"
        :index="index"
        :data="item"
      />
    </div>

    <!-- 업데이트 버튼 -->
    <div class="update-button-container" v-if="controllers?.length > 0">
      <button
        type="button"
        class="btn btn-primary btn-update-firmware"
        :disabled="!canUpdateFirmware"
        @click="onFirmwareUpdateClick"
      >
        <i class="fa-regular fa-circle-down me-2"></i>
        전체 펌웨어 업데이트
      </button>
    </div>
  </div>
  <PopProgress :percent="percent" :visible="visibleProgress" />
</template>

<script setup>
import Axios, { CtrlApi, ScheduleApi } from '@/api';
import {
  ref,
  reactive,
  computed,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import useMqttControl from '@/common/hooks/useMqttControl';
import PopProgress from '@/common/components/popup/PopProgress.vue';
import UpdateControllerItem from "./components/UpdateControllerItem.vue"
import { MQTTTempCommand } from '@/common/constants';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const store = useStore();
const getters = computed(() => store.getters);
const allControllersRaw = computed(
  () => getters.value['home/getControllers'],
);
const controllers = computed(
  () => getters.value['home/getLowVersionControllers'],
);
const checkControllers = computed(
  () => getters.value['home/getCheckControllers'],
);

// 조명제어기 판단 함수 (시리얼에 '_' 포함: M02_/M22_/M24_ …)
const isMTZController = (controller) => {
  return controller._id?.includes('_');
};

/**
 * 표시 순서:
 * - store `getControllers`는 소유 zone 순 × 각 zone.controllers 배열 순(등록/DB 순서).
 *   타입별 정렬·섹션 그룹은 원래 없음.
 * - 여기서 조명제어기(MTZ)를 위에, 온도조절기(T01/T02 등)를 아래로 재배치.
 *   같은 타입 안에서는 기존 상대 순서를 유지(stable sort).
 */
const allControllers = computed(() => {
  const list = allControllersRaw.value || [];
  return [...list].sort((a, b) => {
    const aTemp = isMTZController(a) ? 0 : 1;
    const bTemp = isMTZController(b) ? 0 : 1;
    return aTemp - bTemp;
  });
});

// 전체 펌웨어 업데이트 버튼 활성화 조건
const canUpdateFirmware = computed(() => {
  if (!controllers.value || controllers.value.length === 0) {
    return false;
  }
  
  // 조명제어기만 필터링 (온도조절기는 전체 업데이트에서 제외)
  const lightControllers = controllers.value.filter(item => isMTZController(item));
  
  if (lightControllers.length === 0) {
    return false;
  }
  
  // 온라인 상태인 조명제어기가 하나라도 있으면 활성화
  // checkControllers store: 0=unknown, 1=online, 3=offline
  return lightControllers.some(controller => {
    const status = checkControllers.value[controller._id];
    return status === 1; // 온라인 상태 (store에는 1로 저장됨)
  });
});

// 실제로 모든 장치가 최신 버전인지 확인
const allControllersUpToDate = computed(() => {
  if (!allControllers.value || allControllers.value.length === 0) {
    return false;
  }
  
  // product 정보가 있고, lastVersion이 있는 컨트롤러들만 확인
  const controllersWithProduct = allControllers.value.filter(controller => {
    return controller.lastVersion !== undefined && controller.lastVersion !== null;
  });
  
  // product 정보가 있는 컨트롤러가 없으면 판단 불가
  if (controllersWithProduct.length === 0) {
    return false;
  }
  
  // 모든 컨트롤러가 최신 버전인지 확인
  return controllersWithProduct.every(controller => {
    return controller.version >= controller.lastVersion;
  });
});
const updatingControllers = computed(
  () => getters.value['home/getFirmwareUpdatingControllers'],
);
const updatedControllers = computed(
  () => getters.value['home/getFirmwareUpdatedControllers'],
);
const percent = ref(0);
const visibleProgress = ref(false);
const timeout = 60; // second
let currentSecond = 0;
let timerId = undefined;
let firmwareUpdateCount = 0;

onBeforeMount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: '펌웨어 업데이트',
    isShowZone: false,
    isShowMenu: false,
    isShowRightHome: false,
    isShowProfile: false,
  });
});

// 모든 컨트롤러의 상태를 자동으로 확인하는 함수
const checkAllControllersStatus = async () => {
  if (!allControllers.value || allControllers.value.length === 0) {
    return;
  }

  // 각 컨트롤러에 대해 상태 확인 요청
  allControllers.value.forEach(async (controller) => {
    // 상태 확인 초기화
    store.dispatch("home/updateCheckControllers", { serial: controller._id, value: 0 });
    
    // 조명제어기인 경우
    if (controller._id.split("_").length > 1) {
      mqttControl.onPublishController(controller._id, {
        serial: controller._id,
        cmd: 'p',
        type: 1,
        data: {
          ports: [1],
          requestId: 1
        }
      });
    } else {
      // 온도조절기인 경우 - schedules 조회 후 상태 확인
      try {
        const zones = getters.value['home/getZones'] || [];
        let scheduleValue = '0';
        
        // 해당 컨트롤러의 device 찾기
        for (const zone of zones) {
          for (const group of zone.groups || []) {
            const device = group.devices?.find(d => d.controllerId === controller._id && d.type === 2);
            if (device?._id) {
              const result = await Axios.get(ScheduleApi.SCHEDULES, { deviceId: device._id });
              if (result.resultCode === '0000' && result.resultData?.length > 0) {
                scheduleValue = result.resultData[0].isOn ? '1' : '0';
              }
              break;
            }
          }
          if (scheduleValue !== '0') break;
        }
        
        mqttControl.onPublishTemp(controller._id, MQTTTempCommand.TimerGroupEnable1, scheduleValue);
      } catch (error) {
        console.log('Failed to get schedules for controller:', controller._id, error);
        // schedules 조회 실패 시 기본값 '0' 사용
        mqttControl.onPublishTemp(controller._id, MQTTTempCommand.TimerGroupEnable1, '0');
      }
    }
  });
};

onMounted(() => {
  console.log('[AppUpdatePage] 컴포넌트 마운트됨');
  console.log('[AppUpdatePage] allControllers:', allControllers.value);
  console.log('[AppUpdatePage] allControllers 개수:', allControllers.value?.length || 0);
  console.log('[AppUpdatePage] controllers:', controllers.value);
  console.log('[AppUpdatePage] controllers 개수:', controllers.value?.length || 0);
  
  // 각 컨트롤러의 상세 정보 로그
  if (allControllers.value && allControllers.value.length > 0) {
    console.log('[AppUpdatePage] === 컨트롤러 상세 정보 ===');
    allControllers.value.forEach((controller, index) => {
      console.log(`[AppUpdatePage] 컨트롤러 ${index + 1}:`, {
        _id: controller._id,
        model: controller.model,
        version: controller.version,
        rssi: controller.rssi,
        ssid: controller.ssid,
        updatedAt: controller.updatedAt,
        createdAt: controller.createdAt,
        lastVersion: controller.lastVersion,
        전체데이터: controller
      });
      console.log(`[AppUpdatePage] 컨트롤러 ${index + 1} 키 목록:`, Object.keys(controller || {}));
    });
  } else {
    console.warn('[AppUpdatePage] allControllers가 비어있거나 없습니다.');
  }
  
  // 페이지 진입시 모든 컨트롤러의 상태를 자동으로 확인
  checkAllControllersStatus();
});

onBeforeUnmount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: '',
    isShowZone: true,
    isShowMenu: true,
    isShowRightHome: false,
    isShowProfile: true,
  });
});

const onFirmwareUpdateClick = () => {
  emitter.emit('showPopConfirm', {
    description: '전체 펌웨어를 업데이트 하시겠습니까?',
    okCallback: () => {
      controllers.value.filter(item => isMTZController(item)).forEach((item) => {
        emitter.emit('showLoading');
        console.log({
          serial: item._id,
          cmd: 'f',
          type: 1,
          new_version: item.lastVersion,
          firmware_url: process.env.VUE_APP_PROXY_FIRMWARE_DOMAIN + `/api/${item.downloadUrl}`,
        })
        mqttControl.onPublishFirmware(item._id, {
          serial: item._id,
          cmd: 'f',
          type: 1,
          new_version: item.lastVersion,
          firmware_url: process.env.VUE_APP_PROXY_FIRMWARE_DOMAIN + `/api/${item.downloadUrl}`,
        });

        setTimeout(() => {
          if (!visibleProgress.value) {
            emitter.emit('hideLoading', true);
            emitter.emit('showPopAlert', {
              description: '펌웨어 업데이트에 실패 했습니다.',
            });
          }
        }, 3000);
      });
    },
  });
};

const requestVersionUpdate = (id, params) => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await Axios.patch(CtrlApi.UPDATE_CTRL.replace(":id", id), params);
      if (result.resultCode === '0000') {
        resolve(true);
      } else {
        console.warn(result.resultMessage);
        reject(result.resultMessage);
      }
    } catch (error) {
      console.log(error);
      reject(error);
    }
  });
}

watch(updatingControllers, () => {
  if (updatingControllers.value.length > firmwareUpdateCount) {
    firmwareUpdateCount = updatingControllers.value.length;
  }

  if (updatingControllers.value.length > 0 && !visibleProgress.value) {
    emitter.emit('hideLoading', true);
    percent.value = 0;
    visibleProgress.value = true;
    currentSecond = 0;

    timerId = setInterval(() => {
      currentSecond += timeout / 1000;
      percent.value = parseInt((currentSecond / timeout) * 100);
      // console.log(currentSecond / timeout);
      if (percent.value >= 100) {
        percent.value = 100;
        clearInterval(timerId);
        firmwareUpdateCount = 0;

        setTimeout(() => {
          visibleProgress.value = false;
          emitter.emit('showPopAlert', {
            description: '펌웨어 업데이트에 실패 했습니다.',
          });
        }, 500);
      }
    }, (timeout * 1000) / 1000);
  }
});

// allControllers와 controllers 변화 감시
watch(allControllers, (newVal, oldVal) => {
  console.log('[AppUpdatePage] allControllers 변경 감지');
  console.log('[AppUpdatePage] 이전 개수:', oldVal?.length || 0);
  console.log('[AppUpdatePage] 새로운 개수:', newVal?.length || 0);
  if (newVal && newVal.length > 0) {
    console.log('[AppUpdatePage] 새로운 allControllers 데이터:');
    newVal.forEach((controller, index) => {
      console.log(`[AppUpdatePage] [${index + 1}] ${controller._id}:`, {
        rssi: controller.rssi,
        updatedAt: controller.updatedAt,
        ssid: controller.ssid
      });
    });
  }
}, { deep: true, immediate: true });

watch(controllers, (newVal, oldVal) => {
  console.log('[AppUpdatePage] controllers (업데이트 필요) 변경 감지');
  console.log('[AppUpdatePage] 이전 개수:', oldVal?.length || 0);
  console.log('[AppUpdatePage] 새로운 개수:', newVal?.length || 0);
}, { deep: true });

watch(updatedControllers, () => {
  if (
    firmwareUpdateCount > 0 &&
    updatedControllers.value.length === firmwareUpdateCount
  ) {
    percent.value = 100;
    clearInterval(timerId);
    firmwareUpdateCount = 0;

    setTimeout(async () => {
      visibleProgress.value = false;
      console.log('[AppUpdatePage] 업데이트 완료된 컨트롤러:', updatedControllers.value);
      console.log('[AppUpdatePage] 업데이트 필요 컨트롤러:', controllers.value);
      
      updatedControllers.value.forEach(async (item) => {
        const foundItem = controllers.value.find(ctrl => ctrl._id === item._id);
        if (foundItem) {
          await requestVersionUpdate(item._id, {
            version: foundItem.lastVersion
          });
        }
      });
      await store.dispatch('home/fetchAllZones');
      emitter.emit('showPopAlert', {
        description: '펌웨어 업데이트가 완료 되었습니다.',
      });
    }, 500);
  }
});
</script>

<style scoped>
.firmware-update-page {
  padding-bottom: 2rem;
}

/* 정보 카드 스타일 */
.info-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.info-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.info-card-header {
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  font-weight: 600;
}

.info-card-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #212529;
}

.info-card-body {
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
}

.info-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
}

.info-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
}

.update-count-badge {
  display: inline-block;
  background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  box-shadow: 0 2px 4px rgba(13, 110, 253, 0.3);
}

/* 빈 상태 스타일 */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  margin: 2rem 0;
}

.empty-state-icon {
  font-size: 4rem;
  color: #28a745;
  margin-bottom: 1rem;
}

.empty-state-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  margin-bottom: 0.5rem;
}

.empty-state-description {
  font-size: 0.95rem;
  color: #6c757d;
}

/* 업데이트 버튼 컨테이너 */
.update-button-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background-color: #ffffff;
  border-top: 1px solid #e9ecef;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.btn-update-firmware {
  width: 100%;
  padding: 0.75rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-update-firmware:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(13, 110, 253, 0.4);
  background: linear-gradient(135deg, #0b5ed7 0%, #0a4ca8 100%);
}

.btn-update-firmware:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(13, 110, 253, 0.3);
}

.btn-update-firmware:disabled {
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(108, 117, 125, 0.2);
  cursor: not-allowed;
  opacity: 1;
}

/* 모바일 반응형 */
@media (max-width: 768px) {
  .firmware-update-page {
    padding-bottom: 6rem;
  }

  .info-card-header {
    padding: 0.875rem 1rem;
  }

  .info-card-body {
    padding: 0.875rem 1rem;
  }

  .update-button-container {
    padding: 0.875rem;
  }

  .btn-update-firmware {
    padding: 0.625rem 1rem;
    font-size: 0.95rem;
  }

  .empty-state {
    padding: 2rem 1rem;
  }

  .empty-state-icon {
    font-size: 3rem;
  }
}
</style>
